import { YMaps, Map, Placemark, ZoomControl, FullscreenControl } from '@pbe/react-yandex-maps';
import { Card, Empty, Spin, Tag, Typography, Space } from 'antd';
import { CarOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { t } from '../../i18n';

const { Text } = Typography;

interface DriverLocation {
  lat: number;
  lng: number;
  updatedAt?: string;
  status?: 'ONLINE' | 'OFFLINE' | 'BLOCKED' | 'RESTING';
}

interface DriverLocationMapProps {
  location?: DriverLocation | null;
  driverName?: string;
  isLoading?: boolean;
  height?: number;
}

const YANDEX_API_KEY = import.meta.env.VITE_YANDEX_MAPS_API_KEY || '';

const statusColors: Record<string, string> = {
  ONLINE: '#27AE60',
  OFFLINE: '#7F8C8D',
  BLOCKED: '#E74C3C',
  RESTING: '#F39C12',
};

const statusLabels: Record<string, string> = {
  ONLINE: t('status.online'),
  OFFLINE: t('status.offline'),
  BLOCKED: t('status.blocked'),
  RESTING: t('status.resting'),
};

export const DriverLocationMap = ({
  location,
  driverName,
  isLoading = false,
  height = 400,
}: DriverLocationMapProps) => {
  if (isLoading) {
    return (
      <Card title="Haydovchi joylashuvi">
        <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!location || !location.lat || !location.lng) {
    return (
      <Card title="Haydovchi joylashuvi">
        <Empty
          image={<EnvironmentOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />}
          description="Joylashuv ma'lumoti mavjud emas"
          style={{ height: height - 48, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        />
      </Card>
    );
  }

  const center: [number, number] = [location.lat, location.lng];
  const statusColor = location.status ? statusColors[location.status] : '#1ABC9C';

  return (
    <Card
      title={
        <Space>
          <EnvironmentOutlined />
          <span>Haydovchi joylashuvi</span>
          {location.status && (
            <Tag color={statusColor === '#27AE60' ? 'green' : statusColor === '#E74C3C' ? 'red' : statusColor === '#F39C12' ? 'orange' : 'default'}>
              {statusLabels[location.status]}
            </Tag>
          )}
        </Space>
      }
      extra={
        location.updatedAt && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Yangilangan: {new Date(location.updatedAt).toLocaleString('uz-UZ')}
          </Text>
        )
      }
    >
      <YMaps query={{ apikey: YANDEX_API_KEY, lang: 'uz_UZ' }}>
        <Map
          defaultState={{
            center,
            zoom: 14,
          }}
          width="100%"
          height={height}
          modules={['control.ZoomControl', 'control.FullscreenControl']}
        >
          <ZoomControl />
          <FullscreenControl />
          <Placemark
            geometry={center}
            options={{
              preset: 'islands#circleIcon',
              iconColor: statusColor,
            }}
            properties={{
              hintContent: driverName || 'Haydovchi',
              balloonContentHeader: driverName || 'Haydovchi',
              balloonContentBody: `
                <div style="padding: 8px;">
                  <p><strong>Koordinatalar:</strong></p>
                  <p>Lat: ${location.lat.toFixed(6)}</p>
                  <p>Lng: ${location.lng.toFixed(6)}</p>
                  ${location.updatedAt ? `<p style="color: #7F8C8D; font-size: 12px; margin-top: 8px;">Yangilangan: ${new Date(location.updatedAt).toLocaleString('uz-UZ')}</p>` : ''}
                </div>
              `,
            }}
            modules={['geoObject.addon.balloon', 'geoObject.addon.hint']}
          />
        </Map>
      </YMaps>
    </Card>
  );
};
